import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { fetchData, deleteData, SyncData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import ReactDOM from 'react-dom';
import BillPrint from './Bill';
import '../common.css';

function RentListIndex() {
  const history = useHistory();
  const { isAuthenticated, companyId, token, logout } = useAuth();
  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
    setFromDate(getFormattedStartDate());
    setToDate(getFormattedCurrentDate());
    fetchTableData();
  }, [isAuthenticated, companyId, token, logout]);

  const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
  const getFormattedStartDate = () => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  };

  const fetchTableData = async () => {
    setData([]);
    setLoading(true);
    try {
      const response = await fetchData('RentCollectionsApi', `/CustomerDetails/${companyId}/${fromDate}/${toDate}`, token);
      if (response.responseCode === '1111') {
        setData(response.response_data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        logout();
        history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    deleteData('SaleApi', `${deleteItemId}`, token)
      .then(() => {
        fetchTableData();
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
      })
      .catch((error) => console.error('Error deleting:', error));
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  const filteredData = data.filter((item) =>
    Object.values(item).some((value) =>
      String(value).toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  function formatDate(dateString) {
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')} ${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
  }

  const handlePrint = async (RentcollectionID) => {
    try {
      const response = await fetchData('RentCollectionsApi', `/${RentcollectionID}`, token);
      const labelData = response.response_data;

      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.top = '-10000px';
      iframe.style.left = '-10000px';
      document.body.appendChild(iframe);

      const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
      iframeDocument.open();
      iframeDocument.write('<html><head><style>@media print { header, footer { display: none !important; } }</style></head><body><div id="print-root"></div></body></html>');
      iframeDocument.close();

      const printRoot = iframeDocument.getElementById('print-root');
      ReactDOM.render(
        <BillPrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />,
        printRoot
      );

      // Wait for images to load before printing
      const images = iframeDocument.querySelectorAll('img');
      const imagePromises = Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      });

      await Promise.all(imagePromises);
      
      // Small delay to ensure rendering is complete
      setTimeout(() => {
        iframe.contentWindow.print();
      }, 100);

      await SyncData('RentCollectionsApi', `${RentcollectionID}`, token);
    } catch (error) {
      console.error('Error printing:', error);
    }
  };

  return (
    <div className="page-container">
      {loading && (
        <div className="loading-overlay">
          <CircularProgress size={40} className="loading-spinner" />
          <span className="loading-text">Loading...</span>
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Rent List</h1>
        <div className="header-actions">
          <div className="search-box">
            <SearchIcon />
            <input
              type="text"
              className="search-input"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <input
            type="date"
            className="form-input"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ width: '140px' }}
          />
          <input
            type="date"
            className="form-input"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ width: '140px' }}
          />
          <button className="create-btn" onClick={fetchTableData}>
            <RefreshIcon fontSize="small" />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Bill Date</th>
                <th>Bill No</th>
                <th>Customer</th>
                <th>Contact</th>
                <th>Room</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{index + 1}</td>
                    <td className="cell-secondary">{formatDate(item.BillDate)}</td>
                    <td className="cell-primary">{item.BillNo}</td>
                    <td>{item.customerName}</td>
                    <td>{item.phone}</td>
                    <td><span className="cell-badge">{item.roomName}</span></td>
                    <td style={{ textAlign: 'right', fontWeight: '600', color: '#059669' }}>₹ {item.Amount?.toFixed(2) || '0.00'}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn print" title="Print" onClick={() => handlePrint(item.ID)}>
                          <PrintIcon fontSize="small" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8">
                    <div className="empty-state">
                      <p>No rent records found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Dialog */}
      <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog}>
        <DialogTitle sx={{ fontWeight: 600, color: '#1e3a5f' }}>Delete Record?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#64748b' }}>
            Are you sure you want to delete this record?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <button className="dialog-btn cancel" onClick={closeConfirmDialog}>Cancel</button>
          <button className="dialog-btn confirm" onClick={handleDelete}>Delete</button>
        </DialogActions>
      </Dialog>

    </div>
  );
}

export default RentListIndex;
